import type posthogType from 'posthog-js';
import { getAcquisitionAttribution } from './attribution';
import { clearAnalyticsJourney, createAnalyticsEventId, getOrCreateJourneyId, shouldAliasJourney } from './analyticsIdentity';

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const ANALYTICS_SCHEMA_VERSION = 1;
const ANALYTICS_SCHEMA_STATIC_DISABLED = import.meta.env.VITE_FLAG_ANALYTICS_SCHEMA === 'false';
let postHogInitialized = false;
let posthog: typeof posthogType | null = null;
let postHogLoadPromise: Promise<typeof posthogType | null> | null = null;

const hasAnalyticsConsent = () => typeof window !== 'undefined'
  && localStorage.getItem('fairday_cookie_consent') === 'granted';

const loadPostHog = async (): Promise<typeof posthogType | null> => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return null;
  postHogLoadPromise ||= import('posthog-js').then(module => module.default);
  posthog ||= await postHogLoadPromise;
  return posthog;
};

const FORBIDDEN_ANALYTICS_KEY = /(?:^|_)(?:email|e_mail|name|phone|address|street|zip|postal|dob|birth|ssn|answer|document|signature|password|token|secret|credential|query|search|click_id|gclid|fbclid|session_id|payment_method|card_number|free_text|claim_title|claim_content|overview|description|eligibility|cta_text|bank_name|error|error_message|failed_reason|filing_url|confirmation_id)(?:$|_)/i;
const EMAIL_VALUE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sanitizeAnalyticsValue = (key: string, value: unknown): unknown => {
  if (FORBIDDEN_ANALYTICS_KEY.test(key)) return undefined;
  if (value === null || typeof value === 'undefined') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (key.toLowerCase().includes('url')) {
      try {
        const base = typeof window === 'undefined' ? 'https://fairday.app' : window.location.origin;
        const parsed = new URL(value, base);
        return `${parsed.origin}${parsed.pathname}`.slice(0, 200);
      } catch {
        return undefined;
      }
    }
    if (EMAIL_VALUE.test(value)) return undefined;
    if (value.includes('?') || value.includes('#')) return undefined;
    return value.slice(0, 200);
  }
  if (Array.isArray(value)) {
    const safeValues = value
      .map((item) => sanitizeAnalyticsValue(key, item))
      .filter((item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean');
    return safeValues.length ? safeValues.slice(0, 20) : undefined;
  }
  if (typeof value === 'object') {
    const nested = sanitizeAnalyticsProperties(value as Record<string, unknown>);
    return Object.keys(nested).length ? nested : undefined;
  }
  return undefined;
};

// Function declaration (hoisted) — mutually recursive with sanitizeAnalyticsValue.
export function sanitizeAnalyticsProperties(
  properties?: Record<string, unknown>,
): Record<string, unknown> {
  if (!properties) return {};
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    const safeValue = sanitizeAnalyticsValue(key, value);
    if (typeof safeValue !== 'undefined') sanitized[key] = safeValue;
  }
  return sanitized;
}

/**
 * Initialize PostHog analytics
 * Call this once when the app loads
 */
export const initPostHog = async () => {
  if (postHogInitialized) return;
  if (!POSTHOG_KEY) {
    console.warn('[PostHog] No API key found. Analytics disabled.');
    return;
  }

  const client = await loadPostHog();
  if (!client || postHogInitialized) return;
  client.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    // Route changes are captured explicitly with query strings and fragments removed.
    capture_pageview: false,
    capture_pageleave: false,
    // Fairday handles potentially sensitive eligibility information. Session
    // replay is disabled globally until route-level, consent-aware controls have
    // completed privacy review.
    disable_session_recording: true,
    // DNT is deprecated by most browsers and silently kills ALL tracking.
    // Our cookieless persistence already provides privacy-by-default.
    respect_dnt: false,
    // Use localStorage for session continuity across page navigations.
    // 'memory' loses the user on every page load, making analytics useless.
    persistence: 'localStorage',
    // Explicit events provide a reviewable data contract. Autocapture can leak
    // quiz answer labels and claim context through DOM metadata.
    autocapture: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Bootstrap with feature flags (optional)
    bootstrap: {},
    // Callback when PostHog is loaded
    loaded: (posthog) => {
      if (import.meta.env.DEV) {
        posthog.debug();
      }
    },
  });
  postHogInitialized = true;

  if (import.meta.env.DEV) {
    console.log('[PostHog] Initialized');
  }
};

/**
 * Identify a user (call after login)
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return;

  void initPostHog().then(() => {
    if (!posthog || !postHogInitialized) return;

    const journeyId = getOrCreateJourneyId();
    const anonymousDistinctId = posthog.get_distinct_id();
    if (anonymousDistinctId && anonymousDistinctId !== userId && shouldAliasJourney(journeyId, userId)) {
      posthog.alias(userId, anonymousDistinctId);
    }

    posthog.identify(userId, {
      ...sanitizeAnalyticsProperties(properties),
      analytics_journey_id: journeyId,
      last_seen: new Date().toISOString(),
    });
  });
};

/**
 * Reset user identity (call after logout)
 */
export const resetUser = () => {
  if (posthog && postHogInitialized) posthog.reset();
  clearAnalyticsJourney();
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return;
  void initPostHog().then(() => {
    if (!posthog || !postHogInitialized) return;
    if (ANALYTICS_SCHEMA_STATIC_DISABLED || posthog.getFeatureFlag('funnel-analytics-schema-v1') === false) return;
    posthog.capture(eventName, {
      ...sanitizeAnalyticsProperties(properties),
      ...getAcquisitionAttribution(),
      analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
      analytics_journey_id: getOrCreateJourneyId(),
      analytics_event_id: createAnalyticsEventId(),
      timestamp: new Date().toISOString(),
    });
  });
};

/**
 * Track page view manually (if autocapture is disabled)
 */
export const trackPageView = (pageName?: string) => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return;
  void initPostHog().then(() => {
    if (!posthog || !postHogInitialized) return;
    if (ANALYTICS_SCHEMA_STATIC_DISABLED || posthog.getFeatureFlag('funnel-analytics-schema-v1') === false) return;
    posthog.capture('$pageview', {
      page_name: pageName,
      url: `${window.location.origin}${window.location.pathname}`,
      path: window.location.pathname,
      ...getAcquisitionAttribution(),
      analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
      analytics_journey_id: getOrCreateJourneyId(),
      analytics_event_id: createAnalyticsEventId(),
    });
  });
};

/**
 * Set user properties without identifying
 */
export const setUserProperties = (properties: Record<string, unknown>) => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return;
  void initPostHog().then(() => posthog?.people.set(sanitizeAnalyticsProperties(properties)));
};

/**
 * Check if a feature flag is enabled
 */
export const isFeatureEnabled = (flagKey: string): boolean => {
  if (!POSTHOG_KEY || !postHogInitialized || !posthog) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
};

/**
 * Get feature flag value
 */
export const getFeatureFlag = (flagKey: string): string | boolean | undefined => {
  if (!POSTHOG_KEY || !postHogInitialized || !posthog) return undefined;
  return posthog.getFeatureFlag(flagKey);
};

/**
 * Opt user out of tracking
 */
export const optOut = () => {
  if (!POSTHOG_KEY || !postHogInitialized || !posthog) return;
  posthog.opt_out_capturing();
};

/**
 * Opt user back into tracking
 */
export const optIn = () => {
  if (!POSTHOG_KEY || !hasAnalyticsConsent()) return;
  void initPostHog().then(() => posthog?.opt_in_capturing());
};

/**
 * Check if user has opted out
 */
export const hasOptedOut = (): boolean => {
  if (!POSTHOG_KEY || !postHogInitialized || !posthog) return false;
  return posthog.has_opted_out_capturing();
};

// Pre-defined event names for consistency
export const ANALYTICS_EVENTS = {
  // Auth events
  SIGN_UP: 'signup_completed',
  SIGN_IN: 'user_signed_in',
  SIGN_OUT: 'user_signed_out',

  // Claim events
  CLAIM_VIEWED: 'claim_opened',
  CLAIM_CTA_CLICKED: 'claim_cta_clicked',
  CLAIM_SHARED: 'referral_shared',
  CLAIM_ELIGIBILITY_CHECKED: 'claim_eligibility_checked',
  SETTLEMENT_IMPRESSION: 'settlement_impression',

  // Navigation
  PAGE_VIEWED: 'page_viewed',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked',
  REDIRECT_CONTINUED: 'redirect_continued',
  REDIRECT_CANCELLED: 'redirect_cancelled',

  // Bank connection
  BANK_CONNECTION_STARTED: 'bank_connection_started',
  BANK_CONNECTION_COMPLETED: 'bank_connection_completed',
  BANK_CONNECTION_FAILED: 'bank_connection_failed',
  // Post-payment bank step: offered → linked (or skipped) → what one scan found
  BANK_STEP_SHOWN: 'bank_step_shown',
  BANK_STEP_SKIPPED: 'bank_step_skipped',
  BANK_SCAN_COMPLETED: 'bank_scan_completed',

  // Tools
  ACCIDENT_CALCULATOR_USED: 'accident_calculator_used',
  UNCLAIMED_PROPERTY_SEARCHED: 'unclaimed_property_searched',

  // Engagement
  QUIZ_STARTED: 'quiz_started',
  QUIZ_STEP_VIEWED: 'quiz_step_viewed',
  QUIZ_ANSWERED: 'quiz_answered',
  QUIZ_COMPLETED: 'quiz_completed',
  QUIZ_ABANDONED: 'quiz_abandoned',
  QUIZ_RESULTS_GENERATED: 'quiz_results_generated',
  QUIZ_ACCOUNT_LINKED: 'quiz_account_linked',
  QUIZ_ACCOUNT_LINK_FAILED: 'quiz_account_link_failed',

  // Acquisition and identity handoff
  LANDING_VIEWED: 'landing_viewed',
  LANDING_CTA_CLICKED: 'landing_cta_clicked',
  RESULT_REVEAL_VIEWED: 'result_reveal_viewed',
  SIGNUP_STARTED: 'signup_started',

  // Conversion
  RETAINER_VIEWED: 'retainer_viewed',
  RETAINER_SIGNED: 'retainer_signed',

  // === NEW: Funnel Events ===
  // Onboarding funnel
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_ABANDONED: 'onboarding_abandoned',

  // Claim submission funnel
  CLAIM_FUNNEL_STARTED: 'claim_started',
  CLAIM_DETAILS_ENTERED: 'claim_details_entered',
  CLAIM_INFO_SUBMITTED: 'claim_info_submitted',
  CLAIM_REVIEW_VIEWED: 'claim_review_viewed',
  CLAIM_SUBMITTED: 'claim_submitted',

  // Subscription funnel
  SUBSCRIPTION_PAGE_VIEWED: 'subscription_page_viewed',
  SUBSCRIPTION_PLAN_SELECTED: 'plan_selected',
  SUBSCRIPTION_CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_COMPLETED: 'checkout_completed',
  SUBSCRIPTION_FAILED: 'subscription_failed',
  PAYWALL_VIEWED: 'paywall_viewed',
  ENTITLEMENT_GRANTED: 'entitlement_granted',

  // Activation and retention
  CLAIM_OPENED: 'claim_opened',
  ELIGIBILITY_REVIEW_COMPLETED: 'eligibility_review_completed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  REFUND_ISSUED: 'refund_issued',
  LIFECYCLE_MESSAGE_SENT: 'lifecycle_message_sent',
  LIFECYCLE_MESSAGE_OPENED: 'lifecycle_message_opened',
  LIFECYCLE_MESSAGE_CLICKED: 'lifecycle_message_clicked',
  REFERRAL_SHARED: 'referral_shared',
  REFERRAL_ACCEPTED: 'referral_accepted',

  // FairdayIQ funnel
  FAIRDAYIQ_VIEWED: 'fairdayiq_viewed',
  FAIRDAYIQ_BANK_PROMPT_SHOWN: 'fairdayiq_bank_prompt_shown',
  FAIRDAYIQ_SCAN_STARTED: 'fairdayiq_scan_started',
  FAIRDAYIQ_SCAN_COMPLETED: 'fairdayiq_scan_completed',
  FAIRDAYIQ_MATCH_FOUND: 'fairdayiq_match_found',

  // Revenue events
  REVENUE_EARNED: 'revenue_earned',
  PAYOUT_REQUESTED: 'payout_requested',
  PAYOUT_COMPLETED: 'payout_completed',

  // Feed ranking events (EVS v4)
  FEED_SORT_CHANGED: 'feed_sort_changed',
  FEED_NEAR_MISS_IMPRESSION: 'feed_near_miss_impression',
  FEED_SURPRISE_SHOWN: 'feed_surprise_shown',
} as const;

// ============================================
// FUNNELS CONFIGURATION
// ============================================

/**
 * Define funnels for PostHog analysis
 * These are tracked automatically when events fire in sequence
 */
export const FUNNELS = {
  // Main conversion funnel
  USER_ACQUISITION: {
    name: 'User Acquisition',
    steps: [
      'landing_viewed',
      'quiz_started',
      'quiz_completed',
      'result_reveal_viewed',
      'signup_completed',
      'quiz_account_linked',
      'paywall_viewed',
      'checkout_started',
      'checkout_completed',
      'eligibility_review_completed',
    ],
  },

  // Claim submission funnel
  CLAIM_SUBMISSION: {
    name: 'Claim Submission',
    steps: [
      'claim_opened',          // 1. View claim
      'claim_cta_clicked',     // 2. Click CTA
      'claim_started',         // 3. Start form
      'claim_info_submitted',  // 4. Submit info
      'claim_submitted',       // 5. Complete
    ],
  },

  // Retainer signing funnel
  RETAINER_CONVERSION: {
    name: 'Retainer Conversion',
    steps: [
      'claim_opened',          // 1. View claim
      'retainer_viewed',       // 2. View retainer
      'retainer_signed',       // 3. Sign retainer
    ],
  },

  // Premium subscription funnel
  SUBSCRIPTION_CONVERSION: {
    name: 'Subscription Conversion',
    steps: [
      'subscription_page_viewed',     // 1. View pricing
      'plan_selected',         // 2. Select plan
      'checkout_started',      // 3. Start checkout
      'checkout_completed',    // 4. Complete
    ],
  },

  // FairdayIQ adoption funnel
  FAIRDAYIQ_ADOPTION: {
    name: 'FairdayIQ Adoption',
    steps: [
      'fairdayiq_viewed',            // 1. View section
      'bank_connection_started',      // 2. Start connection
      'bank_connection_completed',    // 3. Complete connection
      'fairdayiq_scan_completed',     // 4. Run scan
      'fairdayiq_match_found',        // 5. Find match
    ],
  },
} as const;

// ============================================
// CONVERSION GOALS
// ============================================

/**
 * Key conversion goals for tracking
 * Use these to measure business outcomes
 */
export const CONVERSION_GOALS = {
  // Primary conversions
  SIGNUP: 'signup_completed',
  CLAIM_SUBMITTED: 'claim_submitted',
  RETAINER_SIGNED: 'retainer_signed',
  SUBSCRIPTION_COMPLETED: 'checkout_completed',

  // Secondary conversions
  QUIZ_COMPLETED: 'quiz_completed',
  BANK_CONNECTED: 'bank_connection_completed',
  FAIRDAYIQ_ACTIVATED: 'fairdayiq_scan_completed',
} as const;

// ============================================
// FUNNEL TRACKING HELPERS
// ============================================

/**
 * Track a funnel step with timing
 */
export const trackFunnelStep = (
  funnelName: string,
  stepName: string,
  stepNumber: number,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('funnel_step_completed', {
    funnel_name: funnelName,
    step_name: stepName,
    step_number: stepNumber,
    ...properties,
  });
};

/**
 * Track funnel completion
 */
export const trackFunnelComplete = (
  funnelName: string,
  totalSteps: number,
  durationMs?: number,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('funnel_completed', {
    funnel_name: funnelName,
    total_steps: totalSteps,
    duration_ms: durationMs,
    ...properties,
  });
};

/**
 * Track funnel abandonment
 */
export const trackFunnelAbandoned = (
  funnelName: string,
  lastStepCompleted: number,
  totalSteps: number,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('funnel_abandoned', {
    funnel_name: funnelName,
    last_step_completed: lastStepCompleted,
    total_steps: totalSteps,
    abandonment_rate: lastStepCompleted / totalSteps,
    ...properties,
  });
};

// ============================================
// REVENUE TRACKING
// ============================================

// posthog-js dropped `people.increment` from its public types (deprecated),
// but the method still exists at runtime on the loaded client.
interface LegacyPeopleApi {
  increment: (prop: string, by?: number) => void;
}

/**
 * Track revenue event (for LTV calculations)
 */
export const trackRevenue = (
  amount: number,
  currency: string = 'USD',
  type: 'claim_payout' | 'subscription' | 'referral',
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('revenue_earned', {
    revenue_amount: amount,
    revenue_currency: currency,
    revenue_type: type,
    ...properties,
  });

  // Also set as user property for LTV tracking
  // The `postHogInitialized` guard above guarantees the client is non-null;
  // the casts are type-only and emit no runtime code.
  ((posthog as typeof posthogType).people as unknown as LegacyPeopleApi).increment('total_revenue', amount);
  ((posthog as typeof posthogType).people as unknown as LegacyPeopleApi).increment(`${type}_revenue`, amount);
};

// ============================================
// USER SEGMENTATION
// ============================================

/**
 * Update user segment properties
 */
export const updateUserSegment = (
  segment: {
    plan?: 'free' | 'premium' | 'trial';
    claimsSubmitted?: number;
    totalEarnings?: number;
    bankConnected?: boolean;
    daysSinceSignup?: number;
    lastActive?: string;
  }
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  const properties: Record<string, unknown> = {};

  if (segment.plan) properties.subscription_plan = segment.plan;
  if (segment.claimsSubmitted !== undefined) properties.claims_submitted = segment.claimsSubmitted;
  if (segment.totalEarnings !== undefined) properties.total_earnings = segment.totalEarnings;
  if (segment.bankConnected !== undefined) properties.bank_connected = segment.bankConnected;
  if (segment.daysSinceSignup !== undefined) properties.days_since_signup = segment.daysSinceSignup;
  if (segment.lastActive) properties.last_active = segment.lastActive;

  (posthog as typeof posthogType).people.set(sanitizeAnalyticsProperties(properties));
};

// ============================================
// A/B TESTING HELPERS
// ============================================

/**
 * Get variant for an experiment
 */
export const getExperimentVariant = (experimentKey: string): string | undefined => {
  if (!POSTHOG_KEY || !postHogInitialized) return undefined;
  const variant = (posthog as typeof posthogType).getFeatureFlag(experimentKey);
  return typeof variant === 'string' ? variant : undefined;
};

/**
 * Track experiment exposure (call when user sees the variant)
 */
export const trackExperimentExposure = (
  experimentKey: string,
  variant: string
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('$experiment_exposure', {
    experiment_key: experimentKey,
    variant: variant,
  });
};

/**
 * Track experiment conversion
 */
export const trackExperimentConversion = (
  experimentKey: string,
  conversionType: string,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;

  trackEvent('experiment_conversion', {
    experiment_key: experimentKey,
    conversion_type: conversionType,
    ...properties,
  });
};

/**
 * Hook into feature flags with callback
 */
export const onFeatureFlags = (callback: () => void) => {
  if (!POSTHOG_KEY || !postHogInitialized) return;
  (posthog as typeof posthogType).onFeatureFlags(callback);
};

export default posthog;
